import {
  PageContainer,
  Button,
  Progress,
  cn,
  CURRENT_CLIENT,
  useOnboarding,
  useEmployerOrganisationOptional,
  EMPLOYER_ORG_CREATE_STEPS,
  EMPLOYER_ORG_SETUP_STEPS,
  clampEmployerOrgSetupStepIndex,
  employerOrgCreateSetupPath,
  employerOrgSetupPath,
  employerOrgSetupStepIndex,
  EmployerOrgNameStep,
  EmployerCompanyBioStep,
  EmployerCompanyLogoStep,
  EmployerPostingPreferencesStep,
  OnboardingStepSkeleton,
  onboardingDismissalStorageKey,
  onboardingHomePathForGroup,
  shouldBackfillEmployerOrganisationSetup,
  useAuth,
} from '@yeshuman/sdk'
import type { EmployerOrgSetupStep, OnboardingStepCompleteResult } from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

const STEP_COMPONENTS = {
  'employer-company-bio-step': EmployerCompanyBioStep,
  'employer-company-logo-step': EmployerCompanyLogoStep,
  'employer-posting-preferences-step': EmployerPostingPreferencesStep,
} as const

const ONBOARDING_PAGE_CLASS = 'flex flex-col min-h-screen bg-background'
const ONBOARDING_CONTENT_CLASS = 'mt-[60px] flex flex-1 flex-col'

/**
 * One continuous onboarding-style journey for an organisation: name → bio → logo → posting
 * preferences. Reached either as "add organisation" (fresh, unsaved org — `:slug` is `new`) or
 * as a resume/continue link for an org that already exists (real `:slug`, name step skipped).
 *
 * The current step is derived entirely from the URL `?step=` param (single source of truth).
 * We deliberately do NOT also track the newly-created slug as component state: an earlier
 * version did, and the two sources (React state vs. router URL state) committed in separate,
 * un-batched renders — the URL would advance to the next step and immediately snap back to
 * `org_name`. Bio/logo/prefs steps already resolve their org from localStorage (written
 * synchronously by `createManagedOrganisation`), so no local "createdSlug" state is needed.
 */
export function EmployerOrganisationSetupWizard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { slug: slugParam } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const stepParam = searchParams.get('step')
  const ctx = useEmployerOrganisationOptional()

  const routeSlug = slugParam?.trim() ?? ''
  const isCreateEntry = !routeSlug || routeSlug === 'new'

  /** Only meaningful for resume mode; create mode resolves its org via localStorage per step. */
  const orgSlug = isCreateEntry ? undefined : routeSlug
  const steps: EmployerOrgSetupStep[] = isCreateEntry ? EMPLOYER_ORG_CREATE_STEPS : EMPLOYER_ORG_SETUP_STEPS

  /** Still on the name step until the org is created (no `?step=` beyond `org_name` yet). */
  const nameStepPending = isCreateEntry && (!stepParam || stepParam === 'org_name')

  const currentStepIndex = useMemo(() => {
    if (nameStepPending) return 0
    if (!stepParam) return 0
    const requested = employerOrgSetupStepIndex(steps, stepParam)
    if (requested < 0) return 0
    return clampEmployerOrgSetupStepIndex(steps, requested)
  }, [nameStepPending, stepParam, steps])

  const { status: onboardingStatus, isLoading: onboardingLoading, completeStep } = useOnboarding()
  const syncAccountOnboarding =
    !onboardingLoading &&
    Boolean(
      onboardingStatus?.has_onboarding &&
        !onboardingStatus.is_completed &&
        onboardingStatus.group_name === 'employer',
    )

  const [isAdvancing, setIsAdvancing] = useState(false)
  const [advanceError, setAdvanceError] = useState<string | null>(null)

  useEffect(() => {
    if (!orgSlug) return
    ctx?.setActiveOrganisationSlug(orgSlug)
  }, [ctx, orgSlug])

  /** Canonicalise the URL to match the resolved step (e.g. missing/unknown `?step=`) — does not drive step state. */
  useEffect(() => {
    if (nameStepPending) return
    if (!isCreateEntry && !orgSlug) return
    const targetStep = steps[currentStepIndex]
    if (!targetStep || stepParam === targetStep.id) return
    const path = isCreateEntry
      ? employerOrgCreateSetupPath(targetStep.id)
      : employerOrgSetupPath(orgSlug!, targetStep.id)
    navigate(path, { replace: true })
  }, [currentStepIndex, isCreateEntry, nameStepPending, navigate, orgSlug, stepParam, steps])

  const goToStepIndex = useCallback(
    (index: number, options?: { replace?: boolean }) => {
      const step = steps[index]
      if (!step) return
      const path = isCreateEntry
        ? employerOrgCreateSetupPath(step.id)
        : orgSlug
          ? employerOrgSetupPath(orgSlug, step.id)
          : null
      if (!path) return
      navigate(path, { replace: options?.replace ?? false })
    },
    [isCreateEntry, navigate, orgSlug, steps],
  )

  const handleOrgCreated = useCallback(async () => {
    void queryClient.invalidateQueries({ queryKey: ['managed-organisations'] })

    if (syncAccountOnboarding) {
      const result = await completeStep('organisation_setup')
      if (!result.success) {
        setAdvanceError(result.error || 'Organisation was created but setup progress could not be saved')
      }
    }

    goToStepIndex(1)
  }, [completeStep, goToStepIndex, queryClient, syncAccountOnboarding])

  /** Backfill: org name was collected in this wizard but account onboarding still tracks `organisation_setup`. */
  useEffect(() => {
    if (!syncAccountOnboarding || onboardingLoading) return
    if (!shouldBackfillEmployerOrganisationSetup(onboardingStatus, { skipWhenNamePending: nameStepPending })) {
      return
    }
    void completeStep('organisation_setup')
  }, [
    completeStep,
    nameStepPending,
    onboardingLoading,
    onboardingStatus?.completed_step_ids,
    syncAccountOnboarding,
  ])

  const finishSetup = useCallback(async () => {
    if (syncAccountOnboarding && user?.email && onboardingStatus?.group_name) {
      localStorage.removeItem(onboardingDismissalStorageKey(user.email, onboardingStatus.group_name))
    }

    navigate(onboardingHomePathForGroup('employer'), { replace: true })
  }, [navigate, onboardingStatus?.group_name, syncAccountOnboarding, user?.email])

  const handleAdvance = useCallback(async (): Promise<OnboardingStepCompleteResult> => {
    const activeStep = steps[currentStepIndex]
    if (!activeStep) {
      return { success: false, error: 'Invalid step' }
    }

    setIsAdvancing(true)
    setAdvanceError(null)

    if (syncAccountOnboarding) {
      const result = await completeStep(activeStep.id)
      setIsAdvancing(false)
      if (!result.success) {
        const message = result.error || 'Could not complete this step'
        setAdvanceError(message)
        return { success: false, error: message }
      }
    } else {
      setIsAdvancing(false)
    }

    const nextIndex = currentStepIndex + 1
    if (nextIndex < steps.length) {
      goToStepIndex(nextIndex)
      return { success: true }
    }

    await finishSetup()
    return { success: true }
  }, [completeStep, currentStepIndex, finishSetup, goToStepIndex, steps, syncAccountOnboarding])

  const handleBack = () => {
    if (currentStepIndex > (isCreateEntry ? 1 : 0)) {
      goToStepIndex(currentStepIndex - 1)
    }
  }

  if (!isCreateEntry && !routeSlug) {
    return (
      <PageContainer maxWidth="2xl" padding="p-4" className={ONBOARDING_PAGE_CLASS}>
        <div className={cn(ONBOARDING_CONTENT_CLASS, 'items-center justify-center')}>
          <p className="text-muted-foreground">Organisation not found.</p>
          <Button className="mt-4" onClick={() => navigate('/employer/organisation')}>
            Back to organisation
          </Button>
        </div>
      </PageContainer>
    )
  }

  if (onboardingLoading) {
    return (
      <PageContainer maxWidth="2xl" padding="p-4" className={ONBOARDING_PAGE_CLASS}>
        <div className={ONBOARDING_CONTENT_CLASS}>
          <OnboardingStepSkeleton ariaLabel="Loading organisation setup" />
        </div>
      </PageContainer>
    )
  }

  const activeStep = steps[currentStepIndex]
  if (!activeStep) {
    return (
      <PageContainer maxWidth="2xl" padding="p-4" className={ONBOARDING_PAGE_CLASS}>
        <div className={cn(ONBOARDING_CONTENT_CLASS, 'items-center justify-center')}>
          <p className="text-muted-foreground">Invalid step</p>
        </div>
      </PageContainer>
    )
  }

  const stepLabel = { current: currentStepIndex + 1, total: steps.length }
  const progress = (stepLabel.current / stepLabel.total) * 100
  const clientName = CURRENT_CLIENT.name || 'Yes Human'
  const canGoBack = currentStepIndex > (isCreateEntry ? 1 : 0)

  const renderStep = () => {
    if (activeStep.id === 'org_name') {
      return <EmployerOrgNameStep isCompleting={isAdvancing} onCreated={handleOrgCreated} />
    }

    const StepComponent = STEP_COMPONENTS[activeStep.component as keyof typeof STEP_COMPONENTS]
    if (!StepComponent) {
      return <p className="text-muted-foreground">Step component not found</p>
    }

    return (
      <StepComponent
        step={activeStep}
        orgSlug={orgSlug}
        onNext={handleAdvance}
        onBack={handleBack}
        isCompleting={isAdvancing}
        canGoBack={canGoBack}
        allowSkipPostingPreferences={activeStep.id === 'posting_preferences' && !syncAccountOnboarding}
      />
    )
  }

  return (
    <PageContainer maxWidth="2xl" padding="p-4" className={ONBOARDING_PAGE_CLASS}>
      <div className={ONBOARDING_CONTENT_CLASS}>
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Step {stepLabel.current} of {stepLabel.total}
            </span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} />
        </div>

        <div className="flex flex-1 flex-col justify-center">
          <div className="w-full">
            <h2 className="mb-2 text-2xl font-bold">
              {activeStep.title.replace('{client_name}', clientName)}
            </h2>
            <p className="mb-6 text-muted-foreground">{activeStep.description}</p>

            {renderStep()}

            {advanceError ? (
              <p className="mt-4 text-sm text-destructive" role="alert">
                {advanceError}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </PageContainer>
  )
}
